/**
 * Warranty Template Edit dialog.
 *
 * A single `useForm`-powered modal that edits the template name and its
 * coverage-attribute default values as they exist today. Replaces the
 * hand-rolled inline name edit on the detail header.
 */

import { useMemo } from 'react'
import { toast } from 'sonner'
import {
  useSaveWarrantyAttributeValues,
  useUpdateWarrantyTemplate,
} from '@/admin/hooks/use-warranties'
import type { WarrantyTemplateUpdate } from '@/admin/hooks/use-warranties'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import type {
  WarrantyAttribute,
  WarrantyTemplate,
} from '@/admin/types/warranties'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField, FormInputType } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

function formInputTypeForWarrantyAttribute(
  attribute: WarrantyAttribute,
): FormInputType {
  switch (attribute.field_type) {
    case 'BOOLEAN':
      return 'checkbox'
    case 'NUMBER':
      return 'number'
    case 'DATE':
      return 'datepicker'
    case 'MONEY':
      return 'currency'
    case 'PHONE':
      return 'phone'
    case 'EMAIL':
      return 'email'
    case 'SELECT':
      return 'select'
    case 'MULTI_SELECT':
      return 'multiSelect'
    case 'ADDRESS':
      return 'address'
    case 'RICH_TEXT':
      return 'rich-text'
    default:
      return 'text'
  }
}

function selectOptionsFromAttribute(attribute: WarrantyAttribute) {
  const raw = attribute.configuration_data?.options
  if (!Array.isArray(raw)) return []
  return raw
    .map((option) => {
      if (typeof option === 'string') return { label: option, value: option }
      if (option && typeof option === 'object') {
        const candidate = option as { label?: unknown; value?: unknown }
        const value = String(candidate.value ?? candidate.label ?? '')
        return { label: String(candidate.label ?? value), value }
      }
      const value = String(option)
      return { label: value, value }
    })
    .filter((option) => option.value)
}

export function EditTemplateDialog({
  open,
  onOpenChange,
  wc,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  wc: WarrantyTemplate
}) {
  const updateTemplate = useUpdateWarrantyTemplate()
  const saveValues = useSaveWarrantyAttributeValues()
  const resourceLookupProviders = useResourceLookupProviders()

  const defaults = useMemo(() => {
    const map = new Map<string, unknown>()
    for (const value of wc.attribute_values ?? []) {
      map.set(value.code_name, value.value)
    }
    return map
  }, [wc.attribute_values])

  const fields = useMemo<FormField[]>(() => {
    const attributes = wc.attributes ?? []
    const attributeFields: FormField[] = attributes.map((attribute) => ({
      type: formInputTypeForWarrantyAttribute(attribute),
      name: `attr__${attribute.code_name}`,
      label: attribute.display_name,
      value: defaults.get(attribute.code_name) ?? '',
      hint: attribute.description || attribute.hint || undefined,
      required: attribute.required ? `${attribute.display_name} is required` : undefined,
      options: selectOptionsFromAttribute(attribute),
      testId: `wt-edit-attr-${attribute.code_name}`,
    }))

    // `is_transferable` and `priority` are generic EAV attributes now, so they
    // arrive in `wc.attributes` and render through `attributeFields` alongside
    // every other coverage attribute - no hardcoded fields for them here.
    return [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        value: wc.name,
        required: 'Name is required',
        testId: 'wt-edit-name',
      },
      ...attributeFields,
    ]
  }, [wc.name, wc.attributes, defaults])

  const { Form, ref } = useForm(
    {
      name: 'warranty-template-edit-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, unknown>
      const name = String(values.name ?? '').trim()

      const attributeValues: Record<string, unknown> = {}
      for (const attribute of wc.attributes ?? []) {
        attributeValues[attribute.code_name] = values[`attr__${attribute.code_name}`]
      }

      // Only send the template columns that actually changed, so an unchanged
      // save is a no-op on the record. Transferability and priority travel with
      // the attribute-value save below, not as template columns.
      const templateUpdate: WarrantyTemplateUpdate = {}
      if (name && name !== wc.name) {
        templateUpdate.name = name
      }

      try {
        if (Object.keys(templateUpdate).length > 0) {
          await updateTemplate.mutateAsync({ uuid: wc.uuid, data: templateUpdate })
        }
        if ((wc.attributes ?? []).length > 0) {
          await saveValues.mutateAsync({ templateUuid: wc.uuid, data: attributeValues })
        }
        toast.success('Warranty template updated')
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to update warranty template')
      }
    },
    [wc.uuid, open],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) ref.current?.resetForm()
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Warranty Template</DialogTitle>
        </DialogHeader>
        <ResourceLookupContextProvider providers={resourceLookupProviders}>
          <div className="max-h-[68vh] overflow-y-auto pr-1">
            <Form className="flex flex-col gap-4 py-2" />
          </div>
        </ResourceLookupContextProvider>
        <DialogFooter>
          <Button
            type="submit"
            form="warranty-template-edit-form"
            disabled={updateTemplate.isPending || saveValues.isPending}
            className="rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
